import { useEffect, useMemo, useRef, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { IDeliveryServicesService, IShipmentDeliveryServicesQueryParams } from '../../@posturinn/client-react';
import ICountryItem from '../../interfaces/countries/countries';
import { IFormSelectOption } from '../../interfaces/forms/forms';
import IPostService from '../../interfaces/forms/postService';
import store from '../../store';

type IService = '' | 'Letter' | 'Parcel';

const shouldFetchDeliveryServices = (
	parcelWeight: number | null,
	service: IService,
	countryCode: string,
	fromPC: string,
	toPC: string,
	height: string,
	width: string,
	length: string
) => {
	const hasWeight = typeof parcelWeight === 'number' && Number.isFinite(parcelWeight) && parcelWeight >= 0;
	const hasAllDimensions = !!(height && width && length);
	const hasNoDimensions = !height && !width && !length;

	return (
		hasWeight &&
		service !== '' &&
		countryCode !== '' &&
		(fromPC !== '0' || countryCode !== 'IS') &&
		(toPC !== '0' || countryCode !== 'IS') &&
		(service === 'Letter' || hasAllDimensions || hasNoDimensions)
	);
};

const hasPartialParcelDimensions = (service: IService, height: string, width: string, length: string) => {
	const hasAllDimensions = !!(height && width && length);
	const hasNoDimensions = !height && !width && !length;
	return service === 'Parcel' && !hasAllDimensions && !hasNoDimensions;
};

const usePostalServices = (
	parcelWeight: number | string,
	service: IService,
	country: ICountryItem,
	fromPostalCode: IFormSelectOption,
	toPostalCode: IFormSelectOption,
	length: string,
	width: string,
	height: string
) => {
	const [servicesLoading, setServicesLoading] = useState(false);
	const [services, setServices]: [IPostService[], any] = useState(null);
	const activeQueryRef = useRef<{ unsubscribe: () => void } | null>(null);
	const requestIdRef = useRef(0);
	const normalizedParcelWeight = useMemo(() => {
		if (parcelWeight === '' || parcelWeight === null || parcelWeight === undefined) {
			return null;
		}

		const parsedWeight = Number(parcelWeight);
		return Number.isFinite(parsedWeight) ? parsedWeight : null;
	}, [parcelWeight]);
	const countryCode = country?.value || '';
	const fromPC = fromPostalCode ? fromPostalCode.value : '0';
	const toPC = toPostalCode ? toPostalCode.value : '0';
	const requestInput = useMemo(
		() => ({
			parcelWeight: normalizedParcelWeight,
			service,
			countryCode,
			fromPC,
			toPC,
			length,
			width,
			height,
		}),
		[normalizedParcelWeight, service, countryCode, fromPC, toPC, length, width, height]
	);
	const [debouncedRequestInput] = useDebounce(requestInput, 300);

	useEffect(() => {
		if (activeQueryRef.current) {
			activeQueryRef.current.unsubscribe();
			activeQueryRef.current = null;
		}

		if (
			!shouldFetchDeliveryServices(
				debouncedRequestInput.parcelWeight,
				debouncedRequestInput.service,
				debouncedRequestInput.countryCode,
				debouncedRequestInput.fromPC,
				debouncedRequestInput.toPC,
				debouncedRequestInput.height,
				debouncedRequestInput.width,
				debouncedRequestInput.length
			)
		) {
			if (
				hasPartialParcelDimensions(
					debouncedRequestInput.service,
					debouncedRequestInput.height,
					debouncedRequestInput.width,
					debouncedRequestInput.length
				)
			) {
				setServicesLoading(false);
				return;
			}
			setServices(null);
			setServicesLoading(false);
			return;
		}

		const currentRequestId = ++requestIdRef.current;
		const fetchDeliveryServices = () => {
			setServices(null);
			setServicesLoading(true);
			const { injector } = store.getState().mobizReducer;
			const deliveryServicesService: IDeliveryServicesService = injector.get(IDeliveryServicesService);
			const isParcel = debouncedRequestInput.service === 'Parcel';
			const hasWeight = typeof debouncedRequestInput.parcelWeight === 'number' && debouncedRequestInput.parcelWeight > 0;
			const query: IShipmentDeliveryServicesQueryParams = {
				senderPostalCode: debouncedRequestInput.fromPC,
				recipientPostalCode: debouncedRequestInput.toPC,
				recipientCountryCode: debouncedRequestInput.countryCode,
				type: debouncedRequestInput.service,
				...(hasWeight
					? {
							weight: String(debouncedRequestInput.parcelWeight),
							weightUnit: isParcel ? 'KG' : 'G',
					  }
					: {}),
				...(isParcel
					? {
							length: debouncedRequestInput.length,
							height: debouncedRequestInput.height,
							width: debouncedRequestInput.width,
							dimensionUnit: 'CM',
					  }
					: {}),
			};
			try {
				const subscription = deliveryServicesService.getShipmentDeliveryServices(query).subscribe({
					next: response => {
						if (currentRequestId !== requestIdRef.current) {
							return;
						}
						setServices(response);
						setServicesLoading(false);
					},
					error: () => {
						if (currentRequestId !== requestIdRef.current) {
							return;
						}
						setServices([]);
						setServicesLoading(false);
					},
				});
				activeQueryRef.current = subscription;
			} catch {
				setServices([]);
				setServicesLoading(false);
			}
		};

		fetchDeliveryServices();

		return () => {
			if (activeQueryRef.current) {
				activeQueryRef.current.unsubscribe();
				activeQueryRef.current = null;
			}
		};
	}, [debouncedRequestInput]);

	return {
		servicesLoading,
		services,
	};
};

export default usePostalServices;
